/**
 * Booking Calendar utilities.
 *
 * Visual calendar widget showing:
 *   - Available / booked / blocked dates
 *   - Price per night (with dynamic pricing)
 *   - Min/max stay requirements
 *
 * Works with Sharetribe's time-slots API data.
 */

export const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Get all dates between start and end (inclusive).
 * @param {Date|string} start
 * @param {Date|string} end
 * @returns {Date[]}
 */
export const getDateRange = (start, end) => {
  const dates = [];
  let current = new Date(start);
  const last = new Date(end);
  current.setUTCHours(0, 0, 0, 0);
  last.setUTCHours(0, 0, 0, 0);
  while (current <= last) {
    dates.push(new Date(current));
    current = new Date(current.getTime() + DAY_MS);
  }
  return dates;
};

/**
 * Build a calendar month grid (6 weeks × 7 days).
 * @param {number} year
 * @param {number} month — 0-indexed
 * @returns {Date[][]}
 */
export const buildMonthGrid = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const startOfGrid = new Date(firstDay);
  startOfGrid.setDate(1 - firstDay.getDay()); // start from Sunday

  const grid = [];
  for (let week = 0; week < 6; week++) {
    const row = [];
    for (let day = 0; day < 7; day++) {
      const d = new Date(startOfGrid);
      d.setDate(startOfGrid.getDate() + week * 7 + day);
      row.push(d);
    }
    grid.push(row);
  }
  return grid;
};

/**
 * Check if date is in a list of booked date strings (YYYY-MM-DD).
 * @param {Date} date
 * @param {string[]} bookedDates
 * @returns {boolean}
 */
export const isBooked = (date, bookedDates) => {
  if (!Array.isArray(bookedDates)) return false;
  const dateStr = date.toISOString().slice(0, 10);
  return bookedDates.includes(dateStr);
};

/**
 * Format date as YYYY-MM-DD.
 * @param {Date} date
 * @returns {string}
 */
export const toDateString = (date) => date.toISOString().slice(0, 10);

/**
 * Validate date range selection.
 * @param {Date|null} start
 * @param {Date|null} end
 * @param {number} [minNights]
 * @param {number} [maxNights]
 * @returns {string|null}
 */
export const validateDateRange = (start, end, minNights, maxNights) => {
  if (!start || !end) return 'Start and end dates required';
  if (end <= start) return 'End date must be after start date';
  const nights = Math.round((end - start) / DAY_MS);
  if (minNights && nights < minNights) return `Minimum stay is ${minNights} night${minNights !== 1 ? 's' : ''}`;
  if (maxNights && nights > maxNights) return `Maximum stay is ${maxNights} nights`;
  return null;
};
